<script lang="ts">
  import { type Product, SaleType } from '$lib/interfaces';
  import TokenMediaViewer from '$lib/components/TokenMediaViewer/Viewer.svelte';
  import { onMount } from 'svelte';
  import { getProductInfo, getProductSlug } from '$lib/product';
  import { formatCurrency } from '$lib/format';
  import LottiePulse from './lottie/LottiePulse.svelte';
  import Avatar from './Avatar.svelte';

  export let currency = 'USD';
  export let product: Product;
  export let testAppId: string | undefined = undefined;
  export let hoverEffects = false;

  let currentDate = new Date();

  $: ({
    isPreview,
    isAvailable,
    isLiveAuction,
    isUrgent,
    typeText,
    priceText,
    remainingText,
    availableInText,
  } = getProductInfo(product, currency, currentDate));

  $: productLink = `/@${product.username}${
    testAppId ? `+test${testAppId}` : ''
  }/products/${getProductSlug(product)}`;

  onMount(() => {
    if (!isAvailable || product.saleType === SaleType.LimitedEdition) return;

    const dateInterval = setInterval(() => {
      currentDate = new Date();

      if (!isAvailable) {
        clearInterval(dateInterval);
      }
    }, 1000);

    return () => clearInterval(dateInterval);
  });
</script>

<div>
  <a href={productLink}>
    <TokenMediaViewer
      autoplay
      square
      shadow
      rounded
      scale={hoverEffects}
      metadata={product.tokenMetadata}
    />
  </a>
  <section>
    <div
      data-chromatic="ignore"
      class="product-label"
      class:is-sold-out={!isAvailable}
      class:is-urgent={isUrgent}
      class:is-live-auction={isLiveAuction}
    >
      <p class="mr-4">
        {#if isPreview}
          Coming soon
        {:else}
          {typeText}

          {#if isLiveAuction && isAvailable}
            <LottiePulse className="h-4 w-4 -ml-0.5" />
          {/if}
        {/if}
      </p>
      <p class="text-right truncate">
        {#if isPreview}
          {availableInText}
        {:else}
          {remainingText}

          {product.saleType === SaleType.LimitedEdition && isAvailable ? 'left' : ''}
        {/if}
      </p>
    </div>
    <div>
      <h2 class="text-ellipsis overflow-hidden">
        <a href={productLink} class="heading-3 text-primary">{product.tokenMetadata.name}</a>
      </h2>
      <h3 class="caption-bold mt-1">
        <Avatar username={product.username} avatarClassName="avatar-sm" />
      </h3>
      <p class="subtitle mt-2">
        {formatCurrency(priceText, currency)}

        {#if isLiveAuction}
          <span class="caption-bold text-grey">
            {#if isAvailable}
              current bid
            {:else}
              winning bid
            {/if}
          </span>
        {/if}
      </p>
    </div>
  </section>
</div>

<style lang="postcss">
  .product-label {
    @apply my-4 rounded-sm border-2 border-current caption-bold px-1.5 pt-0.5 flex justify-between items-center whitespace-nowrap;
  }

  .is-sold-out {
    @apply text-grey;
  }

  .is-live-auction {
    @apply text-red;
  }

  .is-urgent {
    @apply bg-current;
  }

  .is-urgent > p {
    @apply text-inverse;
  }
  .is-urgent.is-live-auction > p {
    @apply text-white;
  }
</style>
